import { forwardRef } from "react";
import { HStack, Button, Text, Box, Flex, Select, IconButton } from "@chakra-ui/react";
import { ArrowLeft, ArrowRight} from "lucide-react";
import type { MetaData, PaginationLink } from "./types";
import { useThemeColors } from "@/hooks/useThemeColors";
import { paginationStyles } from "./paginationStyles";

interface PaginationProps {
  metas: MetaData;
  onClick: (url: string | null) => void;
  onPerPageChange?: (perPage: number) => void;
  className?: string;
}

export const Pagination = forwardRef<HTMLDivElement, PaginationProps>(
  ({ metas, onClick, onPerPageChange }, ref) => {
    const colors = useThemeColors();
    const {
      total,
      links,
      per_page: perPage,
      current_page: currentPage,
      from,
      to,
    } = metas;

    const scrollbarThumb = paginationStyles.colors.scrollbarThumb;
    const scrollbarThumbHover = paginationStyles.colors.scrollbarThumbHover;

    const goToPage = (url: string | null) => {
      if (url) onClick(url);
    };

    const handlePerPageChange = (newPerPage: number) => {
      if (onPerPageChange) {
        onPerPageChange(newPerPage);
      }
    };

    // Calculate actual number of items showing on current page
    const itemsOnCurrentPage = to && from ? (to - from + 1) : perPage;

    // Find Previous and Next links
    const prevLink = links?.find(link => link.label.includes('Previous') || link.label.includes('&laquo;'));
    const nextLink = links?.find(link => link.label.includes('Next') || link.label.includes('&raquo;'));
    
    // Filter out Previous/Next from main pagination
    const pageLinks = links?.filter(
      link => !link.label.includes('Previous') && 
              !link.label.includes('Next') && 
              !link.label.includes('&laquo;') && 
              !link.label.includes('&raquo;')
    ) || [];

    return (
      <Box ref={ref} {...paginationStyles.container}>
        <Flex {...paginationStyles.wrapper}>
          {/* Left side - Show X Results */}
          <HStack {...paginationStyles.leftContainer}>
            <Text {...paginationStyles.labelText}>Show</Text>
            <Select
              {...paginationStyles.select}
              value={itemsOnCurrentPage}
              onChange={(e) => handlePerPageChange(Number(e.target.value))}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </Select>
            <Text {...paginationStyles.labelText}>Results</Text>
          </HStack>

          {/* Right side - Pagination buttons */}
          <Box
            {...paginationStyles.buttonScrollContainer}
            css={{
              ...paginationStyles.scrollbar,
              '&::-webkit-scrollbar-thumb': {
                background: scrollbarThumb,
                borderRadius: '2px',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                background: scrollbarThumbHover,
              },
            }}
          >
            <HStack {...paginationStyles.buttonGroup}>
              {/* Previous button */}
              <IconButton
                aria-label="Previous page"
                icon={<ArrowLeft size={12} />}
                {...paginationStyles.button.base}
                {...paginationStyles.navButton}
                isDisabled={!prevLink?.url}
                onClick={() => goToPage(prevLink?.url || null)}
              />

              {/* Page number buttons */}
              {pageLinks.map((link: PaginationLink, index: number) => {
                const isDisabled = !link.url;
                const isActive = link.active;
                const isEllipsis = link.label === "...";

                if (isEllipsis) {
                  return (
                    <Box key={index} {...paginationStyles.ellipsisBox}>
                      <Text {...paginationStyles.ellipsisText}>
                        {link.label}
                      </Text>
                    </Box>
                  );
                }

                const buttonStyles = isActive 
                  ? paginationStyles.activeButton 
                  : paginationStyles.inactiveButton;

                return (
                  <Button
                    key={index}
                    {...paginationStyles.button.base}
                    {...buttonStyles}
                    isDisabled={isDisabled}
                    onClick={() => goToPage(link.url)}
                  >
                    {link.label.replace(/&[^;]+;/g, "")}
                  </Button>
                );
              })}

              {/* Next button */}
              <IconButton
                aria-label="Next page"
                icon={<ArrowRight size={12} />}
                {...paginationStyles.button.base}
                {...paginationStyles.navButton}
                isDisabled={!nextLink?.url} 
                onClick={() => goToPage(nextLink?.url || null)}
              />
            </HStack>
          </Box>
        </Flex>
      </Box>
    );
  }
);

Pagination.displayName = "Pagination";